import React, { useState } from 'react';
import { Upload, CheckCircle, AlertCircle, XCircle, X } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { FirmwareProgressModal } from './FirmwareProgressModal';
import { clearFirmwareUpdateProgress } from '../redux/device/actions';

export const FirmwareProgressIcon: React.FC = () => {
  const dispatch = useDispatch();
  const firmwareUpdateProgress = useSelector((state: any) => state.deviceData?.firmwareUpdateProgress || {});
  const [isModalOpen, setIsModalOpen] = useState(false);

  const activeUpdates = Object.values(firmwareUpdateProgress).filter((progress: any) => 
    progress.status === 'initiated' || 
    progress.status === 'in_progress' || 
    progress.status === 'completed' || 
    progress.status === 'failed'
  );

  if (activeUpdates.length === 0) {
    return null;
  }

  const completedCount = activeUpdates.filter((p: any) => p.status === 'completed').length;
  const failedCount = activeUpdates.filter((p: any) => p.status === 'failed').length;
  const inProgressCount = activeUpdates.filter((p: any) => 
    p.status === 'initiated' || p.status === 'in_progress'
  ).length;

  const getIcon = () => {
    if (failedCount > 0) {
      return <XCircle className="h-5 w-5 text-white" />;
    }
    if (completedCount > 0 && inProgressCount === 0) {
      return <CheckCircle className="h-5 w-5 text-white" />;
    }
    if (inProgressCount > 0) {
      return <Upload className="h-5 w-5 text-white animate-pulse" />;
    }
    return <Upload className="h-5 w-5 text-blue-500" />;
  };

  const getTooltipText = () => {
    if (failedCount > 0) {
      return `${failedCount} firmware update(s) failed`;
    }
    if (completedCount > 0 && inProgressCount === 0) {
      return `${completedCount} firmware update(s) completed`;
    }
    if (inProgressCount > 0) {
      return `${inProgressCount} firmware update(s) in progress`;
    }
    return 'Firmware update progress';
  };

  return (
    <>
      {/* Overlay to ensure buttons stay clickable */}
      <div 
        className="fixed inset-0 pointer-events-none z-[9998]" 
        style={{ 
          zIndex: 9998,
          pointerEvents: 'none'
        }}
      />
      
      <div className="fixed bottom-6 right-6 z-[9999]" style={{ zIndex: 9999 }}>
        <div className="relative">
          {/* Close button - only show for completed/failed updates (not in progress) */}
          {inProgressCount === 0 && activeUpdates.length > 0 && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Clear only completed/failed updates (not in-progress ones)
                activeUpdates.forEach((update: any) => {
                  if (update.deviceId && (update.status === 'completed' || update.status === 'failed')) {
                    dispatch(clearFirmwareUpdateProgress(update.deviceId));
                  }
                });
              }}
              className="absolute -top-2 -right-2 bg-gray-600 hover:bg-gray-700 text-white rounded-full p-1 shadow-lg z-10 transition-all duration-200 hover:scale-110"
              title="Dismiss all completed/failed updates"
              style={{ pointerEvents: 'auto' }}
            >
              <X className="h-3 w-3" />
            </button>
          )}
          
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsModalOpen(true);
            }}
            className={`group relative rounded-full shadow-lg border p-3 hover:shadow-xl transition-all duration-200 hover:scale-105 cursor-pointer ${
              inProgressCount > 0 
                ? 'bg-blue-500 text-white animate-pulse' 
                : failedCount > 0 
                  ? 'bg-red-500 text-white' 
                  : completedCount > 0 
                    ? 'bg-green-500 text-white' 
                    : 'bg-white border-gray-200'
            }`}
            style={{ 
              zIndex: 9999,
              position: 'relative',
              pointerEvents: 'auto'
            }}
            title={getTooltipText()}
          >
            {getIcon()}
            
            {/* Progress indicator ring */}
            {inProgressCount > 0 && (
              <div className="absolute -inset-1 rounded-full">
                <svg className="w-8 h-8 transform -rotate-90" viewBox="0 0 36 36">
                  <path
                    className="text-white/30"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className="text-white animate-pulse"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="none"
                    strokeDasharray="100, 100"
                    strokeDashoffset="25"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
              </div>
            )}

            {/* Badge with count */}
            <div className={`absolute -top-1 -right-1 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold border-2 border-white ${
              inProgressCount > 0 
                ? 'bg-blue-600 animate-pulse' 
                : failedCount > 0 
                  ? 'bg-red-600' 
                  : 'bg-green-600'
            }`}>
              {activeUpdates.length}
            </div>
          </div>
        </div>
      </div>

      <FirmwareProgressModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </>
  );
};


